// ./src/components/features/EmailTemplates/EmailTemplatesEditModal/EmailTemplatesEditModal.tsx
import React, { useState, useEffect } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Accordion,
  Text,
  Code,
  Alert,
  Switch,
  Select,
  Skeleton,
  Box,
  Group,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import {
  EmailTemplate,
  EmailTemplateInput,
  isSystemTemplate,
  TEMPLATE_TYPES,
} from "@/utils/api";
import { AppDrawer } from "@/components/generic";
import { EmailLayoutSelector } from "@/components/features/EmailLayouts";

export interface EmailTemplatesEditModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Email template to edit (if null/undefined, it's create mode) */
  emailTemplate?: EmailTemplate | null;

  /** Callback when email template should be created */
  onCreateEmailTemplate?: (
    data: EmailTemplateInput
  ) => Promise<EmailTemplate | null>;

  /** Callback when email template should be updated */
  onUpdateEmailTemplate?: (
    id: string,
    data: EmailTemplateInput
  ) => Promise<EmailTemplate | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Whether data is being loaded */
  isLoading?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface EmailTemplateFormData {
  name: string;
  description: string;
  templateType: string;
  emailLayoutId: string | null;
  subject: string;
  htmlContent: string;
  textContent: string;
  fromName: string;
  fromEmail: string;
  replyToEmail: string;
  isDefault: boolean;
  status: number;
}

const initialFormData: EmailTemplateFormData = {
  name: "",
  description: "",
  templateType: TEMPLATE_TYPES.NOTIFICATION,
  emailLayoutId: null,
  subject: "",
  htmlContent: "",
  textContent: "",
  fromName: "",
  fromEmail: "",
  replyToEmail: "",
  isDefault: false,
  status: 100,
};

const templateTypeOptions = [
  { value: TEMPLATE_TYPES.NOTIFICATION, label: "Notification" },
  { value: TEMPLATE_TYPES.CONFIRMATION, label: "Confirmation" },
];

const defaultNotificationTemplate = `<h2>New Form Submission</h2>

<p>You have received a new submission from <strong>{{formName}}</strong>.</p>

<h3>Submission Details</h3>
{{submissionData}}

<p style="color: #666; font-size: 14px;">
  Submitted on {{submissionDate}}<br>
  Submission ID: {{submissionId}}
</p>`;

const defaultConfirmationTemplate = `<h2>Thank You for Your Submission</h2>

<p>Dear {{submitterEmail}},</p>

<p>Thank you for submitting the <strong>{{formName}}</strong> form. We have received your information and will get back to you shortly.</p>

<h3>Your Submission</h3>
{{submissionData}}

<p style="color: #666; font-size: 14px;">
  Submitted on {{submissionDate}}<br>
  Reference: {{submissionId}}
</p>

<p>If you have any questions, please don't hesitate to contact us.</p>`;

// Loading skeleton component
const FormSkeleton: React.FC = () => (
  <Stack gap="md">
    {/* Template Name skeleton */}
    <Box>
      <Skeleton height={14} width={100} mb={8} />
      <Skeleton height={42} radius="sm" />
    </Box>

    {/* Description skeleton */}
    <Box>
      <Skeleton height={14} width={80} mb={8} />
      <Skeleton height={80} radius="sm" />
    </Box>

    {/* Template Type skeleton */}
    <Box>
      <Skeleton height={14} width={90} mb={8} />
      <Skeleton height={42} radius="sm" />
    </Box>

    {/* Switch skeleton */}
    <Group gap="sm">
      <Skeleton height={24} width={44} radius="xl" />
      <Box>
        <Skeleton height={14} width={140} mb={4} />
        <Skeleton height={12} width={280} />
      </Box>
    </Group>

    {/* Accordion sections skeleton */}
    <Stack gap="sm" mt="md">
      {/* Layout accordion */}
      <Box
        style={{
          border: "1px solid var(--mantine-color-gray-3)",
          borderRadius: "8px",
          padding: "12px 16px",
        }}
      >
        <Skeleton height={20} width={60} />
      </Box>

      {/* Content accordion */}
      <Box
        style={{
          border: "1px solid var(--mantine-color-gray-3)",
          borderRadius: "8px",
          padding: "12px 16px",
        }}
      >
        <Skeleton height={20} width={70} mb={16} />

        {/* Expanded content skeleton */}
        <Stack gap="md" mt="md">
          {/* Alert skeleton */}
          <Skeleton height={48} radius="sm" />

          {/* Subject skeleton */}
          <Box>
            <Skeleton height={14} width={50} mb={8} />
            <Skeleton height={42} radius="sm" />
            <Skeleton height={12} width={200} mt={4} />
          </Box>

          {/* HTML Content skeleton */}
          <Box>
            <Skeleton height={14} width={90} mb={8} />
            <Skeleton height={200} radius="sm" />
          </Box>

          {/* Plain Text skeleton */}
          <Box>
            <Skeleton height={14} width={120} mb={8} />
            <Skeleton height={120} radius="sm" />
            <Skeleton height={12} width={240} mt={4} />
          </Box>
        </Stack>
      </Box>
    </Stack>
  </Stack>
);

export const EmailTemplatesEditModal: React.FC<
  EmailTemplatesEditModalProps
> = ({
  opened,
  onClose,
  emailTemplate,
  onCreateEmailTemplate,
  onUpdateEmailTemplate,
  isCreating = false,
  isUpdating = false,
  isLoading = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] =
    useState<EmailTemplateFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string | null | undefined>
  >({});

  const isEditMode = Boolean(emailTemplate) && Boolean(emailTemplate?.id);
  const isSystemTemplateItem = emailTemplate
    ? isSystemTemplate(emailTemplate)
    : false;
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or email template changes
  useEffect(() => {
    if (opened) {
      if (emailTemplate) {
        // Edit mode - populate form with email template data
        setFormData({
          name: emailTemplate.name || "",
          description: emailTemplate.description || "",
          templateType:
            emailTemplate.templateType || TEMPLATE_TYPES.NOTIFICATION,
          emailLayoutId: emailTemplate.emailLayoutId || null,
          subject: emailTemplate.subject || "",
          htmlContent: emailTemplate.htmlContent || "",
          textContent: emailTemplate.textContent || "",
          fromName: emailTemplate.fromName || "",
          fromEmail: emailTemplate.fromEmail || "",
          replyToEmail: emailTemplate.replyToEmail || "",
          isDefault: emailTemplate.isDefault ?? false,
          status: emailTemplate.status ?? 100,
        });
      } else {
        // Create mode - use defaults with template
        setFormData({
          ...initialFormData,
          htmlContent: defaultNotificationTemplate,
        });
      }

      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, emailTemplate, onClearError]);

  // Update default content when template type changes (only in create mode)
  const handleTemplateTypeChange = (value: string | null) => {
    const newType = value || TEMPLATE_TYPES.NOTIFICATION;
    handleFieldChange("templateType", newType);

    // Only auto-fill content if in create mode and content is empty or matches default
    if (!isEditMode) {
      const isDefaultNotification =
        formData.htmlContent === defaultNotificationTemplate ||
        formData.htmlContent === "";
      const isDefaultConfirmation =
        formData.htmlContent === defaultConfirmationTemplate;

      if (
        isDefaultNotification ||
        isDefaultConfirmation ||
        formData.htmlContent === ""
      ) {
        const newContent =
          newType === TEMPLATE_TYPES.CONFIRMATION
            ? defaultConfirmationTemplate
            : defaultNotificationTemplate;
        handleFieldChange("htmlContent", newContent);
      }
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Template name is required";
    }

    if (!formData.templateType.trim()) {
      newErrors.templateType = "Template type is required";
    }

    if (!formData.htmlContent.trim()) {
      newErrors.htmlContent = "HTML content is required";
    }

    // Email validation
    if (
      formData.fromEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.fromEmail)
    ) {
      newErrors.fromEmail = "Please enter a valid email address";
    }

    if (
      formData.replyToEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.replyToEmail)
    ) {
      newErrors.replyToEmail = "Please enter a valid email address";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (
    field: keyof EmailTemplateFormData,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: EmailTemplateInput = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        templateType: formData.templateType.trim(),
        emailLayoutId: formData.emailLayoutId || null,
        subject: formData.subject.trim() || null,
        htmlContent: formData.htmlContent,
        textContent: formData.textContent.trim() || null,
        fromName: formData.fromName.trim() || null,
        fromEmail: formData.fromEmail.trim() || null,
        replyToEmail: formData.replyToEmail.trim() || null,
        isDefault: formData.isDefault,
        status: formData.status,
      };

      if (isEditMode && emailTemplate && onUpdateEmailTemplate) {
        await onUpdateEmailTemplate(emailTemplate.id || "", submitData);
      } else if (!isEditMode && onCreateEmailTemplate) {
        await onCreateEmailTemplate(submitData);
      }

      onClose();
    } catch (error) {
      console.error("Email template submission error:", error);
    }
  };

  // Get the error to display for a field
  const getDisplayError = (field: string): string | null | undefined => {
    return validationErrors[field];
  };

  // Determine title based on mode and loading state
  const getTitle = () => {
    if (isLoading) {
      return "Loading...";
    }
    if (isSystemTemplateItem) {
      return "View System Template";
    }
    return isEditMode ? "Edit Email Template" : "Create Email Template";
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={getTitle()}
      position="right"
      size="lg"
      closable={!isSubmitting && !isLoading}
      loading={isSubmitting}
      errors={lastError}
      actionType={
        isLoading ? "close" : isSystemTemplateItem ? "close" : "save-cancel"
      }
      onConfirm={isSystemTemplateItem || isLoading ? undefined : handleSubmit}
    >
      {isLoading ? (
        <FormSkeleton />
      ) : (
        <Stack gap="md">
          {/* System template notice */}
          {isSystemTemplateItem && (
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="blue"
              variant="light"
            >
              This is a system template and cannot be modified. You can
              duplicate it to create your own customized version.
            </Alert>
          )}

          {/* Basic Information */}
          <TextInput
            label="Template Name"
            placeholder="Enter template name"
            value={formData.name}
            onChange={(e) => handleFieldChange("name", e.target.value)}
            error={getDisplayError("name")}
            required
            withAsterisk
            size="md"
            readOnly={isSystemTemplateItem}
          />

          <Textarea
            label="Description"
            placeholder="Enter template description (optional)"
            value={formData.description}
            onChange={(e) => handleFieldChange("description", e.target.value)}
            minRows={2}
            maxRows={4}
            autosize
            size="md"
            readOnly={isSystemTemplateItem}
          />

          <Select
            label="Template Type"
            placeholder="Select type"
            data={templateTypeOptions}
            value={formData.templateType}
            onChange={handleTemplateTypeChange}
            error={getDisplayError("templateType")}
            required
            withAsterisk
            size="md"
            disabled={isSystemTemplateItem}
            allowDeselect={false}
          />

          <Switch
            label="Set as default template"
            description="Use this template by default for its type when no specific template is selected"
            checked={formData.isDefault}
            onChange={(e) =>
              handleFieldChange("isDefault", e.currentTarget.checked)
            }
            size="md"
            disabled={isSystemTemplateItem}
          />

          {/* Accordion Sections */}
          <Accordion
            variant="separated"
            radius="md"
            defaultValue={isEditMode ? null : "layout"}
          >
            {/* Layout Selection */}
            <Accordion.Item value="layout">
              <Accordion.Control>
                <Text fw={500}>Layout</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <Text size="sm" c="dimmed">
                    Select an email layout to wrap this template. The layout
                    provides the outer HTML structure, and this template's
                    content will be inserted into the{" "}
                    <Code>{"{{messageBody}}"}</Code> placeholder.
                  </Text>

                  <EmailLayoutSelector
                    label="Email Layout"
                    value={formData.emailLayoutId}
                    onChange={(layoutId) =>
                      handleFieldChange("emailLayoutId", layoutId)
                    }
                    placeholder="Select a layout (optional)"
                    description="Leave empty to send template content without a layout wrapper"
                    disabled={isSystemTemplateItem}
                    required
                    size="md"
                  />
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            {/* Content */}
            <Accordion.Item value="content">
              <Accordion.Control>
                <Text fw={500}>Content</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <Alert
                    icon={<IconInfoCircle size={16} />}
                    color="gray"
                    variant="light"
                  >
                    <Text size="sm">
                      Available variables: <Code>{"{{formName}}"}</Code>,{" "}
                      <Code>{"{{formDescription}}"}</Code>,{" "}
                      <Code>{"{{submissionData}}"}</Code>,{" "}
                      <Code>{"{{submissionDate}}"}</Code>,{" "}
                      <Code>{"{{submissionId}}"}</Code>,{" "}
                      <Code>{"{{currentYear}}"}</Code>
                    </Text>
                  </Alert>

                  <TextInput
                    label="Subject"
                    placeholder="Email subject line (supports variables)"
                    value={formData.subject}
                    onChange={(e) =>
                      handleFieldChange("subject", e.target.value)
                    }
                    description="Example: New submission from {{formName}}"
                    size="md"
                    readOnly={isSystemTemplateItem}
                  />

                  <Textarea
                    label="HTML Content"
                    placeholder="Enter HTML template..."
                    value={formData.htmlContent}
                    onChange={(e) =>
                      handleFieldChange("htmlContent", e.target.value)
                    }
                    error={getDisplayError("htmlContent")}
                    required
                    withAsterisk
                    minRows={12}
                    maxRows={20}
                    autosize
                    size="md"
                    styles={{
                      input: {
                        fontFamily: "monospace",
                        fontSize: "13px",
                      },
                    }}
                    readOnly={isSystemTemplateItem}
                  />

                  <Textarea
                    label="Plain Text Content"
                    placeholder="Enter plain text version (optional)..."
                    description="Used for email clients that don't support HTML"
                    value={formData.textContent}
                    onChange={(e) =>
                      handleFieldChange("textContent", e.target.value)
                    }
                    minRows={6}
                    maxRows={12}
                    autosize
                    size="md"
                    styles={{
                      input: {
                        fontFamily: "monospace",
                        fontSize: "13px",
                      },
                    }}
                    readOnly={isSystemTemplateItem}
                  />
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </Stack>
      )}
    </AppDrawer>
  );
};
